import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * RejectCandidateModal — recruiter-driven rejection with confirmation + a required reason.
 *
 * Shared by the candidate overview list (Action column) and the interview result modal.
 * On success the candidate leaves every active view of the job (funnel_stage='Rejected') and is
 * only reachable via the "Rejected" filter. The backend gates scheduled candidates (409) until
 * their interview time passes — that message is surfaced inline here.
 */
const RejectCandidateModal = ({ open, candidate, searchId, sessionId, onClose, onRejected }) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const primary = settings?.theme?.colors?.primary || '#4f46e5';

  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const name = candidate?.name || candidate?.candidate_name || candidate?.email || 'this candidate';
  const email = candidate?.email || candidate?.candidate_email || '';
  // Sourced candidates (e.g. LinkedIn) may have no email — fall back to the DB id.
  const rawId = candidate?.id ?? candidate?.candidate_id ?? '';
  const candidateId = /^\d+$/.test(String(rawId)) ? String(rawId) : '';

  const close = () => { setReason(''); setError(''); setBusy(false); onClose?.(); };

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) { setError('Please enter a reason for the rejection.'); return; }
    if (!email && !candidateId) { setError('This candidate can’t be identified (no email or id on record).'); return; }
    setBusy(true);
    setError('');
    try {
      await aiAutomationApi.post('/candidates/reject', {
        candidate_email: email || undefined,
        candidate_id: candidateId || undefined,
        search_id: String(searchId || ''),
        session_id: String(sessionId || ''),
        reason: trimmed,
      });
      onRejected?.();
      close();
    } catch (e) {
      setError(e?.message || 'Could not reject the candidate. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const panelBg = isDark ? '#0f172a' : '#ffffff';

  return createPortal(
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl shadow-2xl"
        style={{ backgroundColor: panelBg }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4" style={{ backgroundColor: '#ef44440f' }}>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Reject candidate</h2>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{name}{email ? ` · ${email}` : ''}</p>
          </div>
          <button type="button" onClick={close} aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="px-5 py-4">
          {/* Warning */}
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-900/15 dark:text-rose-300">
            <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v4m0 4h.01M10.3 3.86l-8.07 14A1.5 1.5 0 003.53 20h16.94a1.5 1.5 0 001.3-2.14l-8.07-14a1.5 1.5 0 00-2.6 0z" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span>This removes the candidate from this job — they won't appear in any active view. If they were already contacted, a rejection email is sent.</span>
          </div>

          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Reason for rejection</label>
          <textarea
            value={reason}
            onChange={(e) => { setReason(e.target.value); setError(''); }}
            rows={4}
            autoFocus
            placeholder="e.g. Skills don't match the role requirements"
            className="mt-1.5 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-300 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-100"
          />

          {error && <p className="mt-3 text-xs font-medium text-red-500">{error}</p>}

          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={close}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={busy || !reason.trim()}
              className="rounded-lg bg-rose-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-45">
              {busy ? 'Rejecting…' : 'Reject candidate'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RejectCandidateModal;
